/**
 * K5 soát B7 / B9: lưu chi nhánh chỉ gửi LUẬT chủ tiệm thực sự đổi. Web cũ luôn gửi cả ba (ngày đặt trước, đặt trước ít
 * nhất, hạn đổi / huỷ) từ giá trị lúc TẢI — tab mở lâu lưu tên / địa chỉ là ghi đè luật chủ vừa đổi ở tab khác. Máy chủ
 * đã `COALESCE` (thiếu trường = giữ nguyên) nên chỉ cần không gửi.
 *
 * Mọi giá trị gửi được kẹp vào đúng khoảng máy chủ nhận (khớp `chiNhanhSchema` + CHECK của DB): vượt khoảng thì 400,
 * mà trước đây câu 400 nói "nhập tên chi nhánh" (sai chỗ).
 */
import {
  DAT_TOI_THIEU_MAC_DINH_PHUT,
  DAT_TRUOC_MAC_DINH_NGAY,
  HAN_DOI_HUY_MAC_DINH_PHUT,
} from './text/theo-nganh.ts'

export interface LuatChiNhanh {
  ngayDatTruoc: number
  datTruocToiThieuPhut: number
  hanHuyPhut: number
}

/** Khoảng máy chủ nhận (apps/api `chiNhanhSchema`). */
export const KHOANG_LUAT = {
  ngayDatTruoc: { min: 1, max: 365 },
  datTruocToiThieuPhut: { min: 0, max: 1440 },
  hanHuyPhut: { min: 0, max: 10_080 },
} as const

const MAC_DINH: LuatChiNhanh = {
  ngayDatTruoc: DAT_TRUOC_MAC_DINH_NGAY,
  datTruocToiThieuPhut: DAT_TOI_THIEU_MAC_DINH_PHUT,
  hanHuyPhut: HAN_DOI_HUY_MAC_DINH_PHUT,
}

function kep(k: keyof LuatChiNhanh, v: number): number {
  const { min, max } = KHOANG_LUAT[k]
  return Math.min(max, Math.max(min, v))
}

/** Luật của một chi nhánh đọc từ máy chủ; trường thiếu (máy chủ cũ) → mặc định. */
export function luatCuaChiNhanh(
  cn: { ngayDatTruoc?: number; datTruocToiThieuPhut?: number; hanHuyPhut?: number } | null,
): LuatChiNhanh {
  return {
    ngayDatTruoc: cn?.ngayDatTruoc ?? MAC_DINH.ngayDatTruoc,
    datTruocToiThieuPhut: cn?.datTruocToiThieuPhut ?? MAC_DINH.datTruocToiThieuPhut,
    hanHuyPhut: cn?.hanHuyPhut ?? MAC_DINH.hanHuyPhut,
  }
}

/**
 * Các trường luật cần gửi: chỉ trường khác `goc` (giá trị lúc tải / lúc lưu gần nhất). `goc = null` (chi nhánh mới) so
 * với mặc định của máy chủ. Mỗi giá trị gửi đi đã được kẹp vào khoảng hợp lệ.
 */
export function luatCanGui(hienTai: LuatChiNhanh, goc: LuatChiNhanh | null): Partial<LuatChiNhanh> {
  const nen = goc ?? MAC_DINH
  const ra: Partial<LuatChiNhanh> = {}
  for (const k of ['ngayDatTruoc', 'datTruocToiThieuPhut', 'hanHuyPhut'] as const) {
    if (hienTai[k] !== nen[k]) ra[k] = kep(k, hienTai[k])
  }
  return ra
}

/** Ba ô luật trên màn + luật gốc đã biết của máy chủ (để chỉ gửi phần đổi). */
export interface TrangThaiLuat {
  o: LuatChiNhanh
  goc: LuatChiNhanh | null
}

/** Mở một chi nhánh (hoặc chi nhánh mới = `null`): ô lấy từ chi nhánh, gốc = chính nó (mới = chưa có gốc). */
export function moChiNhanh(
  cn: { ngayDatTruoc?: number; datTruocToiThieuPhut?: number; hanHuyPhut?: number } | null,
): TrangThaiLuat {
  return { o: luatCuaChiNhanh(cn), goc: cn === null ? null : luatCuaChiNhanh(cn) }
}

/**
 * Sau khi lưu thành công: đặt lại CẢ BA ô lẫn gốc từ danh sách máy chủ vừa trả. Chỉ đặt gốc mà để ô cũ thì
 * lần lưu sau tab cũ gửi số cũ đè luật máy khác vừa đổi (K5 vòng 2, B3).
 */
export function sauKhiLuu(
  ds: readonly {
    id: string
    ngayDatTruoc?: number
    datTruocToiThieuPhut?: number
    hanHuyPhut?: number
  }[],
  id: string,
): TrangThaiLuat {
  const luat = luatCuaChiNhanh(ds.find((c) => c.id === id) ?? null)
  return { o: luat, goc: luat }
}
